import _ from 'lodash';
import QuestionSetsViewModel from './QuestionSetsViewModel';

/**
 * Retrieves questionSet from QuestionSets metadata
 * @ignore
 * @param {string} qsName
 * @param {object} metadata
 * @throws Will throw an error if questionSet does not exist in QuestionSets metadata
 * @returns {object} - The single question from questionSets metadata
 */
function getQuestionSet(qsName, metadata) {
    const questionSet = metadata[qsName];

    if (_.isUndefined(questionSet)){
        return;
    }
    if (!questionSet || !questionSet.orderedQuestions) {
        throw new Error(`There is no question set loaded with the name ${qsName}`);
    }

    return questionSet;
}

/**
 * @memberof module:gw-portals-questionsets-js
 */
class QuestionSetsParser {
    /**
     * @throws Will throw an error if questionSetsSubmissionValue is not provided
     * @param {object} questionSetsSubmissionValue
     * @param {object} metadata
     * @param {function} translator
     */
    constructor(questionSetsSubmissionValue, metadata, translator) {
        // eslint-disable-next-line no-underscore-dangle
        this._viewModel = {};
        this.translator = translator;

        if (
            !_.isObject(questionSetsSubmissionValue)
            && !_.isArray(questionSetsSubmissionValue)
        ) {
            throw new Error('A model object is required for storing answers');
        }

        if (_.isArray(questionSetsSubmissionValue)) {
            questionSetsSubmissionValue.forEach((submissionValue) => {
                // eslint-disable-next-line no-underscore-dangle
                this._createViewModel(submissionValue, metadata);
            });
        } else {
            // eslint-disable-next-line no-underscore-dangle
            this._createViewModel(questionSetsSubmissionValue, metadata);
        }
    }

    /**
     * Creates viewModel based on questionSet
     * @method
     * @param {object} submissionValue - The submission value object
     * @param {string} submissionValue.code - QuestionSet code
     * @param {array} submissionValue.answers - QuestionSet available answers
     * @param {object} metadata
     * @returns {object} - viewModel
     */
    // eslint-disable-next-line consistent-return, no-underscore-dangle
    _createViewModel(submissionValue, metadata) {
        // if view model already created then just return that object
        if (submissionValue.aspects
            && submissionValue.questions
            && _.isArray(submissionValue.questions)) {
            return submissionValue;
        }

        const questionSet = getQuestionSet(submissionValue.code, metadata);

        !_.isUndefined(questionSet) && questionSet.orderedQuestions.sort((a, b) => a.order - b.order)
            .forEach((question) => {
                const answer = submissionValue.answers[question.code];

                if (typeof answer === 'boolean') {
                    // eslint-disable-next-line no-param-reassign
                    submissionValue.answers[question.code] = answer ? 'true' : 'false';
                }

                // eslint-disable-next-line no-underscore-dangle
                this._viewModel[question.code] = new QuestionSetsViewModel(
                    question, submissionValue, this.translator
                );

                const values = question.choices.map((choice) => {
                    return {
                        code: choice.choiceCode,
                        name: this.translator({
                            id: choice.displayKey,
                            defaultMessage: choice.choiceCode
                        })
                    };
                });

                // eslint-disable-next-line no-underscore-dangle
                _.extend(this._viewModel[question.code].aspects.availableValues, values);
            });
        // eslint-disable-next-line no-underscore-dangle
        this._viewModel = _.merge({}, this._viewModel, submissionValue);
    }

    /**
     * Returns the viewModel created via parser
     * @method
     * @returns {ViewModel} - The viewModel type based on questionSets
     */
    get viewModel() {
        // eslint-disable-next-line no-underscore-dangle
        return this._viewModel;
    }
}

export default QuestionSetsParser;
